import { getClientConfig } from '../configuration/client-instance';
import { buildHeaders } from '../requestBuilder/build-headers';
import { buildUrl } from '../requestBuilder/build-url';
import type { IRequestOptions } from '../types';
import { NetworkError } from './network-error';
import { TimeoutError } from './timeout-error';

export interface IMakeRawRequestParams {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  body?: unknown;
  options?: IRequestOptions;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: HTTP client error handling
export async function makeRawRequest({
  method,
  path,
  body,
  options = {},
}: IMakeRawRequestParams): Promise<Response> {
  const config = getClientConfig();
  const url = buildUrl({ path, options });
  const headers = await buildHeaders({ options });
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const requestBody = isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined;
  if (isFormData) {
    Reflect.deleteProperty(headers, 'Content-Type');
  }

  const controller = new AbortController();
  const timeout = options.timeout || config.timeout || 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: requestBody,
      signal: controller.signal,
      ...({ cache: 'no-store', credentials: 'include' } as RequestInit),
    });

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.name === 'AbortError') {
      throw new TimeoutError(timeout);
    }

    throw new NetworkError(
      `Network request failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      error instanceof Error ? error : undefined,
    );
  }
}
